import React, { ReactNode, useEffect, useMemo, useRef } from "react";

import { GoogleMapsOverlay } from "@deck.gl/google-maps";

import {
  LOWER_DECK_OVERLAY_ZINDEX,
  TERRITORIES_DECK_OVERLAY_ZINDEX,
  UPPER_DECK_OVERLAY_ZINDEX,
} from "@app/util/map/consts";

import createDeckLayerRegistry from "./createDeckLayerRegistry";
import { DeckOverlaysContext, IDeckOverlaysContext } from "./DeckOverlaysContext";
import { DeckOverlayType } from "./DeckSlot";
import syncDeckOverlay from "./syncDeckOverlay";

interface DeckOverlaysProviderProps {
  children: ReactNode;
  map: google.maps.Map;
}

const createOverlays = (): Record<DeckOverlayType, GoogleMapsOverlay> => ({
  [DeckOverlayType.LOWER]: new GoogleMapsOverlay({
    interleaved: false,
    layers: [],
    pickAsync: "async",
    style: { zIndex: String(LOWER_DECK_OVERLAY_ZINDEX) },
  }),
  [DeckOverlayType.TERRITORIES]: new GoogleMapsOverlay({
    interleaved: false,
    layers: [],
    // Picks through a buffer-readback path instead of glReadPixels: ~35% cheaper per pick (7.6ms -> 4.7ms).
    // It still stalls the main thread on WebGL; only a WebGPU device reads truly deferred.
    pickAsync: "async",
    style: { zIndex: String(TERRITORIES_DECK_OVERLAY_ZINDEX) },
    // One canvas pixel per CSS pixel skips 4x the fragments on retina: dropped frames fell 55% -> 44%
    // panning. Fills get softer than the tiles; raise toward 2 if that stops being acceptable.
    useDevicePixels: 1,
  }),
  [DeckOverlayType.UPPER]: new GoogleMapsOverlay({
    interleaved: false,
    layers: [],
    style: { zIndex: String(UPPER_DECK_OVERLAY_ZINDEX) },
  }),
});

const DeckOverlaysProvider = ({ children, map }: DeckOverlaysProviderProps) => {
  const mapRef = useRef(map);
  mapRef.current = map;
  const frameRef = useRef<null | number>(null);

  const state = useMemo(() => {
    const overlays = createOverlays();
    const registry = createDeckLayerRegistry();
    const attached: Record<DeckOverlayType, boolean> = {
      [DeckOverlayType.LOWER]: false,
      [DeckOverlayType.TERRITORIES]: false,
      [DeckOverlayType.UPPER]: false,
    };
    return { attached, overlays, registry };
  }, []);

  const context = useMemo<IDeckOverlaysContext>(() => {
    const { attached, overlays, registry } = state;
    const scheduleFlush = () => {
      if (frameRef.current !== null) {
        return;
      }
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = null;
        registry.takeChangedOverlays().forEach((type) => {
          attached[type] = syncDeckOverlay(
            overlays[type],
            mapRef.current,
            registry.getLayers(type),
            attached[type]
          );
        });
      });
    };
    return {
      overlays,
      removeLayers: (slot, key) => {
        registry.removeLayers(slot, key);
        scheduleFlush();
      },
      setLayers: (slot, key, layers) => {
        registry.setLayers(slot, key, layers);
        scheduleFlush();
      },
    };
  }, [state]);

  useEffect(
    () => () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
      Object.values(state.overlays).forEach((overlay) => overlay.finalize());
    },
    [state]
  );

  return <DeckOverlaysContext.Provider value={context}>{children}</DeckOverlaysContext.Provider>;
};

export default DeckOverlaysProvider;
